import Papa from 'papaparse';
import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';
import { newestLoad } from './lineFix';
import { num } from './morningReports';

/*
  THE ACCOUNT EDGE FILE. Ordering stage 5, 12 October 2026, on his word: "then
  start stage 5".

  **What it is**, from his walkthrough of 4 October: a run for one office goes
  into Account Edge as **several customer sales, one per vendor being ordered
  from, plus one for everything moving from Austin**, all under that office's
  Account Edge customer. He then makes each vendor's purchase order from its
  sale, by hand, inside Account Edge.

  **The shape is his ITEMSALE.csv exactly**: the same 72 columns in the same
  order and spelling, a byte-order mark, Windows line endings, and a row of
  empty fields between one customer sale and the next. **Only the fields his
  sample fills are filled**, his answer of 12 October ("only certain fields
  will need to have data entered on the export"): the four address lines,
  Item Number, Quantity, Price, Discount and Card ID. Account Edge fills the
  rest itself.

  - **Item Number** is the item's main Account Edge number.
  - **Price** is Account Edge's own Selling Price for it, from the newest stored
    Account Edge list, as his sample does; empty, and flagged, if it has none.
  - **Discount** is the office's, from its Account Edge customer details. His
    answer: discount rules are set per Account Edge customer.
  - **Freight** is left empty. His answer: *"there will be rules setup to
    handle freight it is not as easy as 1 rule for everything."*

  Built from the run **as last saved**; skipped lines and lines with nothing to
  send are left out, and the file is refused while any line is held.
*/

export const ITEMSALE_COLUMNS = ['Co./Last Name', 'First Name', 'Addr 1 - Line 1', '           - Line 2', '           - Line 3', '           - Line 4', 'Invoice #', 'Date', 'Customer PO', 'Ship Via', 'Delivery Status', 'Item Number', 'Quantity', 'Description', 'Price', 'Discount', 'Total', 'Job', 'Comment', 'Journal Memo', 'Salesperson Last Name', 'Salesperson First Name', 'Shipping Date', 'Tax Code', 'Tax Amount', 'Freight Amount', 'Tax on Freight', 'Freight Tax Amount', 'Sale Status', 'Currency Code', 'Exchange Rate', 'Terms - Payment is Due', '           - Discount Days', '           - Balance Due Days', '           - Early Payment Discount', '           - Late Payment Monthly Charge', 'Referral Source', 'Amount Paid', 'Payment Method', 'Payment Notes', 'Name on Card', 'Last 4 Digits on Card', 'Expiration Date', 'Address (AVS)', 'ZIP (AVS)', 'Credit Card Swiped', 'Authorization Code', 'Check Number', 'Category', 'Location ID', 'Card Verification (CVV2) Used', 'Card ID', 'Record ID', 'Web Order', 'Web Order #', 'Shipping Details - Service', 'Tracking Number', 'Shipping Details - Notes', 'Shipping Details - Date Shipped', 'Total Weight (lb)', 'Total Weight (kg)', 'Notes', 'Department ID', 'Department Allocation Amount', 'Department Allocation Percent', 'Job Allocation Amount', 'Job Allocation Percent', 'Commission Rate Level', 'Serial Number', 'Salesperson Record ID', 'Use Web Pay', 'Kit/Component Indicator'];

const AUSTIN = 'From Austin';

/*
  **The customer sales, worked out from the saved run.** Pure, so it can be
  tested. Each vendor being ordered from is a sale, in vendor order, then one
  sale for everything moving from Austin, which can hold items from several
  vendors. Returns the sales and every reason the file cannot be made yet.
*/
export function planSales({ lines, location, ae }) {
  const problems = [];
  const warnings = [];
  if (!location) problems.push('This office has no Account Edge customer and address in OnHand, so the file has nowhere to send it.');
  const held = lines.filter(l => l.route === 'hold');
  if (held.length) problems.push(held.length + ' line' + (held.length === 1 ? ' is' : 's are') + ' held for you. Decide ' + (held.length === 1 ? 'it' : 'them') + ' and save the run first.');
  if (lines.some(l => !l.route)) problems.push('The run has not been saved. Save it first.');

  const sales = new Map();
  for (const l of [...lines].sort((a, b) => a.line_no - b.line_no)) {
    if (l.route !== 'order' && l.route !== 'transfer') continue;
    const qty = Number(l.final_quantity);
    if (!(qty > 0)) continue;
    const info = ae.get(l.id) || {};
    if (!info.number) { problems.push('Line ' + l.line_no + ' (' + l.final_item_number + ') has no Account Edge number.'); continue; }
    if (info.price == null) warnings.push('Line ' + l.line_no + ' (' + info.number + ') has no selling price in Account Edge, so Price is left empty.');
    const key = l.route === 'transfer' ? AUSTIN : (info.vendor || 'No vendor');
    if (!sales.has(key)) sales.set(key, { name: key, austin: l.route === 'transfer', vendor_id: l.route === 'transfer' ? null : info.vendor_id || null,
      sale_key: l.route === 'transfer' ? 'austin' : 'vendor:' + (info.vendor_id || key), lines: [] });
    sales.get(key).lines.push({ id: l.id, line_no: l.line_no, item: info.number, qty, price: info.price });
  }
  const list = [...sales.values()].sort((a, b) => (a.austin - b.austin) || a.name.localeCompare(b.name));
  const discount = location ? Number(location.discount) || 0 : 0;
  for (const s of list) s.value = s.lines.reduce((t, x) => t + (x.price || 0) * x.qty * (1 - discount), 0);
  if (!list.length && !problems.length) problems.push('Nothing in this run is ordered or moved from Austin, so there is no file to make.');
  return { sales: list, problems, warnings, discount };
}

const money = n => '$' + Number(n).toFixed(2);

/* The file itself, as his sample writes it. */
export function itemSaleCsv({ sales, location, discount }) {
  const blank = ITEMSALE_COLUMNS.map(() => '');
  const rows = [];
  sales.forEach((s, i) => {
    if (i) rows.push(blank);
    for (const x of s.lines) {
      const r = ITEMSALE_COLUMNS.map(() => '');
      const set = (col, v) => { r[ITEMSALE_COLUMNS.indexOf(col)] = v == null ? '' : String(v); };
      set('Addr 1 - Line 1', location.addr_line_1);
      set('           - Line 2', location.addr_line_2);
      set('           - Line 3', location.addr_line_3);
      set('           - Line 4', location.addr_line_4);
      set('Item Number', x.item);
      set('Quantity', x.qty);
      set('Price', x.price == null ? '' : money(x.price));
      set('Discount', Math.round(discount * 100) + '%');
      set('Card ID', location.card_id);
      rows.push(r);
    }
  });
  return '\ufeff' + Papa.unparse({ fields: ITEMSALE_COLUMNS, data: rows }, { newline: '\r\n' });
}

/*
  **What the file needs from the database**: the office's Account Edge
  customer details (matched on its card and customer name, because one card
  can have many addresses), and for each line the item's main Account Edge
  number, vendor, and Account Edge selling price.
*/
export async function loadFileInputs(job, lines) {
  const w = job.destination || {};
  let location = null;
  if (w.id) {
    const { data: wh, error } = await supabase.from('warehouses').select('account_edge_customer, account_edge_card_id').eq('id', w.id).maybeSingle();
    if (error) return { error };
    if (wh && wh.account_edge_card_id && wh.account_edge_customer) {
      const { data: locs, error: lErr } = await supabase.from('order_locations')
        .select('code, card_id, addr_line_1, addr_line_2, addr_line_3, addr_line_4, discount').eq('card_id', wh.account_edge_card_id);
      if (lErr) return { error: lErr };
      const want = wh.account_edge_customer.trim().toUpperCase();
      location = (locs || []).find(l => String(l.addr_line_1 || '').trim().toUpperCase() === want) || null;
    }
  }
  const ids = [...new Set(lines.map(l => l.final_catalog_item_id).filter(Boolean))];
  const byItem = new Map();
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase.from('org_catalog').select('catalog_item_id, vendor, account_edge_number').in('catalog_item_id', ids.slice(i, i + 150));
    if (error) return { error };
    for (const r of data || []) byItem.set(r.catalog_item_id, r);
  }
  const vendorOf = new Map();
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase.from('catalog_items').select('id, vendor_id').in('id', ids.slice(i, i + 150));
    if (error) return { error };
    for (const r of data || []) vendorOf.set(r.id, r.vendor_id);
  }
  const numbers = [...new Set([...byItem.values()].map(r => r.account_edge_number).filter(Boolean))];
  const prices = new Map();
  const load = await newestLoad('account_edge');
  if (load && numbers.length) {
    for (let i = 0; i < numbers.length; i += 150) {
      const { data, error } = await fetchAll(() => supabase.from('account_edge_items_raw')
        .select('"Item Number","Selling Price"').eq('load_id', load.id).in('"Item Number"', numbers.slice(i, i + 150)).order('source_row'));
      if (error) return { error };
      for (const r of data || []) {
        const p = num(r['Selling Price']);
        if (p != null && p > 0) prices.set(String(r['Item Number']).trim().toUpperCase(), p);
      }
    }
  }
  const ae = new Map();
  for (const l of lines) {
    const it = byItem.get(l.final_catalog_item_id);
    if (!it) continue;
    ae.set(l.id, { number: it.account_edge_number, vendor: it.vendor, vendor_id: vendorOf.get(l.final_catalog_item_id) || null,
      price: it.account_edge_number ? (prices.get(it.account_edge_number.trim().toUpperCase()) ?? null) : null });
  }
  return { error: null, location, ae };
}

/* The download itself, named for the job, the office and the day. */
export function downloadItemSale(csv, job) {
  const office = (job.destination && (job.destination.onhand_name || job.destination.name)) || 'office';
  const day = new Date().toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'Account Edge ' + job.reference + ' ' + office + ' ' + day + '.csv';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}
